import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import * as XLSX from 'xlsx';
import { MediaService } from 'src/app/services/media.service';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import { TransactionSearchRequest } from 'src/app/shared/models/transactions/transaction-search-request';
import { formatDate } from '@angular/common';
import { TransactionStatus } from 'src/app/shared/models/transactions/transaction-status';
import { HttpErrorResponse } from '@angular/common/http';
import { TransactionDealService } from 'src/app/services/transaction-deal.service';
import { TransactionDeals } from 'src/app/shared/models/transaction-deals/transaction-deal';
import { LookupService } from 'src/app/services/lookup.service';
import { TransactionStatusUtil } from 'src/app/utils/transaction-status-util';
import { TradeMediaInfo } from 'src/app/shared/models/general/trade-media-info';
import { DatePipe } from '@angular/common';
import { BadTransactionCount } from 'src/app/shared/models/transactions/bad-transaction-count';
import { SystemService } from 'src/app/services/system.service';
import {
  buildTradeDocsBreakdownLines,
  TradeDocsBreakdownLine,
  tradeDocsPinsAndUsdCopyText,
} from 'src/app/utils/trade-docs-display';

interface TradeDocsDisplayRow {
  id: number;
  trade: Transaction;
  infoText: string;
  statusName: string;
  statusHelp: string;
  statusClass: string;
  canEditFia: boolean;
  hasDocs: boolean;
  breakdownLines: TradeDocsBreakdownLine[];
}

@Component({
  selector: 'app-trades-in-progress',
  templateUrl: './trades-in-progress.component.html',
  styleUrls: ['./trades-in-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TradesInProgressComponent implements OnInit {
  @ViewChild('userFiaPinModal', { static: true }) userFiaPinModal: any;

  busy: boolean = true;
  downloadBusy: boolean = false;
  modalBusy: boolean = true;
  showError: boolean = false;
  showModalError: boolean = false;
  errorMsg: string = 'Oops! Looks like something went wrong, please contact support if this issue persists';
  errorResponse: HttpErrorResponse;

  lastMessage: string;

  transactionSearchRequest: TransactionSearchRequest = new TransactionSearchRequest();
  transactions: Transaction[] = [];
  displayRows: TradeDocsDisplayRow[] = [];
  selectedTransaction: Transaction = new Transaction();
  selectedTransactionDeal: TransactionDeals = new TransactionDeals();
  selectedTransactionStatus: TransactionStatus = new TransactionStatus();

  showTradeEditModal: boolean = false;
  showFiaSdaEditModal: boolean = false;

  transactionStatuses: TransactionStatus[] = [];
  filterStatuses: TransactionStatus[] = [];

  updatedDate: Date;
  dealRate: number = 0;
  roleId: number = 0;
  copyPinsMessage: string = '';

  constructor(
    public settingsService: SettingsService,
    public transactionService: TransactionService,
    public dealService: TransactionDealService,
    public mediaService: MediaService,
    private lookupService: LookupService,
    private datePipe: DatePipe,
    private systemService: SystemService,
    private cd: ChangeDetectorRef,
    private clipboard: Clipboard
  ) {}

  setVisualStatusClass(trade: Transaction): string {
    const names: string[] = [];
    if (trade.recentlyUpdated) {
      names.push('update-success');
    }
    if (trade.error) {
      names.push('update-fail');
    }
    if (trade.isCritical || TransactionStatus.criticalStatuses.includes(trade.transactionStatusId)) {
      names.push('critical');
    }
    if (TransactionStatus.emergencyStatuses.includes(trade.transactionStatusId)) {
      names.push('emergency');
    }
    if (TransactionStatus.semiUrgentStatuses.includes(trade.transactionStatusId)) {
      names.push('semiUrgent');
    }
    if (!trade.isCritical && TransactionStatus.isNormalStatus(trade.transactionStatusId)) {
      names.push('normal');
    }
    if (trade.transactionStatusId === 241) {
      names.push('authorised');
    }
    return names.join(' ');
  }

  private checkForCriticalAndHandleDelay(trade: Transaction) {
    const foundIndex = this.transactions.findIndex((x) => x.id == trade.id);
    let originalTrade = this.transactions[foundIndex];

    if (!originalTrade) {
      return;
    }

    if (originalTrade.transactionStatusId != trade.transactionStatusId) {
      originalTrade.transactionStatusId = trade.transactionStatusId;
      originalTrade.isCritical = false;
      this.refreshView();
      return;
    }

    if (this.isTradeInAttemptingStatus(trade) == false) {
      return;
    }

    if (this.IsTradeTooOld(trade)) {
      originalTrade.isCritical = true;
      this.refreshView();
      return;
    }

    setTimeout(() => {
      this.GetTradeAndCheckForCriticalStatus(trade.id);
    }, 1000 * 30);
  }

  private isTradeInAttemptingStatus(trade: Transaction) {
    const attemptingToBookDealStatus = 228;
    if (trade.transactionStatusId != attemptingToBookDealStatus) {
      return false;
    }

    return true;
  }

  private IsTradeTooOld(trade: Transaction) {
    if (this.dealsExist(trade)) {
      if (this.getDifferenceInMilliseconds(trade) > 1000 * 60) {
        return true;
      }
    }

    return false;
  }

  private getDifferenceInMilliseconds(trade: Transaction): number {
    const deal = trade.transactionDeals[0];
    const lastUpdatedDate = new Date(deal.lastUpdated);
    const currentDate = new Date();

    const differenceInMilliseconds = currentDate.getTime() - lastUpdatedDate.getTime();

    return differenceInMilliseconds;
  }

  public dealHasLastMessage(trade: Transaction) {
    if (TransactionStatus.isNormalStatus(trade.transactionStatusId)) {
      return false;
    }

    if (TransactionStatus.isNoneStatus(trade.transactionStatusId)) {
      return false;
    }
    if (this.dealsExist(trade)) {
      const deal = trade.transactionDeals[0];
      if (deal.lastMessage) {
        this.lastMessage = deal.lastMessage;
        return true;
      }
    }

    this.lastMessage = '';
    return false;
  }

  private dealsExist(trade: Transaction): boolean {
    const deals = trade?.transactionDeals;
    if (!deals?.length) {
      return false;
    }
    return true;
  }

  private getDealRetryCount(trade: Transaction): number {
    if (this.dealsExist(trade)) {
      return trade.transactionDeals[0].retryCount;
    }
    return -1;
  }

  public buildTradeInfoText(trade: Transaction): string {
    let info = `Id: ${trade.id}`;
    const retryCount = this.getDealRetryCount(trade);

    if (retryCount > -1) {
      info += ` | Retries: ${retryCount}`;
    }
    const startDate = this.datePipe.transform(trade.transactionStartDate, 'dd MMM - hh:mm');
    info += ` | User: ${trade.userId} | ${startDate}`;

    return info;
  }

  trackByTradeId(_index: number, row: TradeDocsDisplayRow): number {
    return row.id;
  }

  trackByBreakdownId(_index: number, line: TradeDocsBreakdownLine): string {
    return line.id;
  }

  private toDisplayRow(trade: Transaction): TradeDocsDisplayRow {
    return {
      id: trade.id,
      trade,
      infoText: this.buildTradeInfoText(trade),
      statusName: this.GetStatusDisplayName(trade.transactionStatusId),
      statusHelp: this.GetHoverHelpText(trade.transactionStatusId),
      statusClass: this.setVisualStatusClass(trade),
      canEditFia: trade.transactionStatusId < 300,
      hasDocs: (trade.supportingMedia?.length || 0) > 0,
      breakdownLines: buildTradeDocsBreakdownLines(trade),
    };
  }

  private refreshView() {
    this.displayRows = this.transactions.map((trade) => this.toDisplayRow(trade));
    this.cd.markForCheck();
  }

  copyPinsAndUsd() {
    const text = tradeDocsPinsAndUsdCopyText(this.displayRows.flatMap((row) => row.breakdownLines));
    if (!text) {
      this.copyPinsMessage = 'No pins to copy';
      this.cd.markForCheck();
      return;
    }

    const pending = this.clipboard.beginCopy(text);
    let remainingAttempts = 3;
    const attempt = () => {
      const result = pending.copy();
      if (!result && --remainingAttempts) {
        setTimeout(attempt);
      } else {
        this.copyPinsMessage = 'Copied pins & USD';
        this.cd.markForCheck();
        setTimeout(() => {
          this.copyPinsMessage = '';
          this.cd.markForCheck();
        }, 3000);
        pending.destroy();
      }
    };
    attempt();
  }

  ngOnInit(): void {
    this.busy = true;
    this.GetTransactionStatuses();
  }

  private loadSettings() {
    this.systemService.loadPollUntilAuthorised().subscribe(() => {
      this.finishLoadSettings();
    });
  }

  private finishLoadSettings() {
    this.settingsService.ValidateGlobalVars().then(() => {
      this.SetupUserTradingSearchRequest();
      this.SearchTrades();
      this.roleId = this.settingsService.userRoles[0].roleId;
      this.cd.markForCheck();
    });
  }

  private SetupUserTradingSearchRequest() {
    this.transactionSearchRequest.brokerId = this.settingsService.brokerUsers[0].brokerId;
    this.transactionSearchRequest.todaysTradesOnly = true;
    this.transactionSearchRequest.transactionStatusId = this.filterStatuses[1].id;
  }

  SearchTrades() {
    this.GetBadTransactionCounts();
    this.busy = true;
    this.cd.markForCheck();
    this.transactionService.SearchTransactions(this.transactionSearchRequest).subscribe({
      next: (response) => {
        this.transactions = response;
        this.transactions.forEach((trade) => {
          this.checkForCriticalAndHandleDelay(trade);
        });
        this.busy = false;
        this.showError = false;
        this.refreshView();
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
        this.cd.markForCheck();
      },
    });
  }

  badStatusCounts: BadTransactionCount[] = [];
  badStatusListToShow: number[] = [20, 50, 60, 90, ...TransactionStatus.emergencyStatuses, ...TransactionStatus.criticalStatuses];

  GetBadTransactionCounts() {
    this.transactionService.FindBadTransactions().subscribe({
      next: (response) => {
        this.badStatusCounts = response?.filter((x) => this.badStatusListToShow.includes(x.statusId)) || [];
        this.cd.markForCheck();
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  getStatusName(statusId: number): string {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);

    return `${status.name}`;
  }

  getBadgeColorForStatus(statusId: number): string {
    if (TransactionStatus.emergencyStatuses.includes(statusId)) {
      return 'red';
    }

    if (TransactionStatus.criticalStatuses.includes(statusId)) {
      return 'red';
    }

    return 'amber darken-2';
  }

  GetTradeAndCheckForCriticalStatus(id: number) {
    console.log('fetching new status for trade ' + id);
    this.transactionService.GetTransactionById(id).subscribe({
      next: (trade: Transaction) => {
        this.checkForCriticalAndHandleDelay(trade);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  DownloadFile(id: number) {
    this.busy = true;
    this.cd.markForCheck();
    this.mediaService.DownloadMedia(id).subscribe({
      next: (response) => {
        this.mediaService.Download(response);
        this.busy = false;
        this.cd.markForCheck();
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
        this.cd.markForCheck();
      },
    });
  }

  DownloadFiles(allFiles: boolean = false) {
    this.downloadBusy = true;
    this.cd.markForCheck();

    var selectedTransactions: Transaction[] = [];
    if (allFiles) {
      selectedTransactions = this.transactions;
    } else {
      selectedTransactions = this.transactions.filter((x) => x.selected == true);
    }

    if (selectedTransactions.length == 0 && allFiles == false) {
      this.showError = true;
      this.errorMsg = 'Please select at least 1 trade';
      this.downloadBusy = false;
      this.cd.markForCheck();
      return;
    }

    var tradeMediaInfo: TradeMediaInfo[] = [];
    selectedTransactions.forEach((element) => {
      var mediaInfo: TradeMediaInfo = {
        fullName: element.fullName,
        mediaIds: [],
      };
      element.supportingMedia.forEach((el) => {
        mediaInfo.mediaIds.push(el.id);
      });
      tradeMediaInfo.push(mediaInfo);
    });

    this.mediaService.DownloadMediaList(tradeMediaInfo).subscribe({
      next: (response) => {
        this.mediaService.Download(response);
        this.showError = false;
        this.downloadBusy = false;
        this.cd.markForCheck();
      },
      error: (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.downloadBusy = false;
        this.cd.markForCheck();
      },
    });
  }

  onShowFiaPinsModal(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.userFiaPinModal.show();
  }

  onEditFiaPins(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.showFiaSdaEditModal = true;
    this.cd.markForCheck();
  }

  updateOriginalTradeFromFiaSdaEdit(editedTransaction: Transaction) {
    const foundIndex = this.transactions.findIndex((x) => x.id === editedTransaction.id);
    if (foundIndex === -1) {
      return;
    }

    let original = this.transactions[foundIndex];

    // Update the transaction with new data from the save response
    // The service now properly maps the response including transactionUserFiaPins with dollarAmountUsed
    original.fiaAmount = editedTransaction.fiaAmount;
    original.sdaAmount = editedTransaction.sdaAmount;
    original.fiaDollarAmount = editedTransaction.fiaDollarAmount;
    original.sdaDollarAmount = editedTransaction.sdaDollarAmount;
    original.transactionUserFiaPins = editedTransaction.transactionUserFiaPins;
    original.transactionStatusId = editedTransaction.transactionStatusId;

    this.selectedTransaction = original;
    this.refreshView();
  }

  sdaUsed(trade: Transaction): boolean {
    if (trade.sdaAmount > 0) {
      return true;
    }
    return false;
  }

  GetStatusDisplayName(statusId: number) {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);
    return status.name;
  }

  GetHoverHelpText(statusId: number) {
    const status = TransactionStatusUtil.FindById(this.transactionStatuses, statusId);
    return status.helpText;
  }

  IsTradeEditable(trade: Transaction) {
    const statusId = trade.transactionStatusId;
    const retryCount = this.getDealRetryCount(trade);

    if (statusId == 230 && retryCount < 6) {
      return false;
    }

    return TransactionStatus.canEditTradeList.includes(statusId);
  }

  EditTransactionAndDeal(transaction: Transaction) {
    this.busy = true;
    this.cd.markForCheck();
    this.dealService.GetDealByTransactionId(transaction.id).subscribe({
      next: (deal: TransactionDeals) => {
        this.selectedTransactionDeal = deal;
        this.selectedTransaction = transaction;
        this.selectedTransactionStatus = TransactionStatusUtil.FindById(this.transactionStatuses, transaction.transactionStatusId);
        this.showTradeEditModal = true;
        this.busy = false;
        this.cd.markForCheck();
      },
      error: (error) => {
        transaction.error = error;
        this.handleModalError(transaction);
        this.busy = false;
        this.refreshView();
      },
    });
  }

  GetTransactionStatuses() {
    this.lookupService.GetTransactionStatuses().subscribe({
      next: (statusList: TransactionStatus[]) => {
        this.transactionStatuses = statusList;
        this.BuildFilterStatuses();
        this.loadSettings();
        this.cd.markForCheck();
      },
      error: (error) => {
        this.errorResponse = error;
        this.showModalError = true;
        this.busy = false;
        this.cd.markForCheck();
      },
    });
  }

  adminStatuses: { id: number; name: string; helpText: string }[] = [
    {
      id: -2,
      name: 'Prebooking',
      helpText: 'All trades in prebooking (Dooya portion)',
    },
    {
      id: -1,
      name: 'Book Now Received',
      helpText: 'All trades that have received a book now message',
    },
    {
      id: 0,
      name: 'Not Complete',
      helpText: 'Show all incomplete statuses',
    },
  ];

  BuildFilterStatuses() {
    if (this.roleId != 1) {
      this.filterStatuses = this.adminStatuses as TransactionStatus[];
      return;
    }
    this.filterStatuses = this.transactionStatuses;
  }

  Extract(idselector: string) {
    var filename = 'InProgressTransactions';
    let element = document.getElementById(idselector);
    var aoa: any[][] = [this.transactions, [0]];
    //aoa[0][0] = this.transactions ;
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, filename + formatDate(new Date(), 'yyyyMMdd_HHmm', 'en') + '.xlsx');
  }

  updateOriginalTrade(editedTransaction: Transaction) {
    const foundIndex = this.transactions.findIndex((x) => x.id === editedTransaction.id);
    let original = this.transactions[foundIndex];

    original.transactionStatusId = editedTransaction.transactionStatusId;
    original = editedTransaction;

    this.selectedTransaction = original;
    this.selectedTransactionDeal = original.transactionDeals[0];

    original.updateSuccess = true;
    original.recentlyUpdated = true;
    this.refreshView();
    setTimeout(() => {
      original.recentlyUpdated = false;
      this.refreshView();
    }, 3000);
  }

  handleModalError(originalTransaction: Transaction) {
    this.errorResponse = originalTransaction.error;
    this.showModalError = true;
    this.cd.markForCheck();
  }

  popupError(error: HttpErrorResponse) {
    //Note: the spread operator here ... creates a new instance of the error so that the onChanges can detect the change in the errorcomponent
    this.errorResponse = { ...error };
    this.showModalError = true;
    this.cd.markForCheck();
  }
}
